/**
 * Locale registry — the single place that knows what languages exist and how each one behaves.
 *
 * Adding a language means adding a row here plus its catalogue under `locales/<code>/`. Nothing
 * else in the app should carry a list of language codes.
 *
 * Design: docs/43-I18N-ARABIC-DESIGN.md §3.
 */

export type LocaleCode = 'en' | 'ar-AE';

export type Direction = 'ltr' | 'rtl';

export interface LocaleDefinition {
  code: LocaleCode;
  /** English name — for admin screens and logs. */
  englishName: string;
  /** The language's own name — this is what a user picking a language must see. */
  nativeName: string;
  direction: Direction;
  /**
   * The BCP-47 tag handed to `Intl.*`. Deliberately NOT the same as `code`.
   *
   * Arabic pins two Unicode extensions:
   *   `-u-nu-latn`   Western digits (0123), not Arabic-Indic (٠١٢٣).
   *   `-u-ca-gregory` Gregorian calendar.
   *
   * Both are decisions, not defaults — see docs/43-I18N-ARABIC-DESIGN.md §2. Gulf business and
   * financial usage is Western-digit (UAE banks, WPS/SIF files and government portals all are),
   * and pinning the calendar stops any engine resolving an Arabic tag to an Islamic default.
   */
  intlLocale: string;
  /** dayjs locale name, registered by `dayjsArabic.ts` for Arabic. */
  dayjsLocale: string;
  /** How this locale's catalogue resolves when a key is missing, in order. */
  fallback: LocaleCode[];
}

export const DEFAULT_LOCALE: LocaleCode = 'en';

export const LOCALES: Record<LocaleCode, LocaleDefinition> = {
  en: {
    code: 'en',
    englishName: 'English',
    nativeName: 'English',
    direction: 'ltr',
    intlLocale: 'en',
    dayjsLocale: 'en',
    fallback: [],
  },
  'ar-AE': {
    code: 'ar-AE',
    englishName: 'Arabic (UAE)',
    nativeName: 'العربية',
    direction: 'rtl',
    intlLocale: 'ar-AE-u-nu-latn-ca-gregory',
    dayjsLocale: 'ar-ae',
    fallback: ['en'],
  },
};

export const LOCALE_CODES = Object.keys(LOCALES) as LocaleCode[];

export const isLocaleCode = (value: unknown): value is LocaleCode =>
  typeof value === 'string' && Object.prototype.hasOwnProperty.call(LOCALES, value);

export const getLocale = (code: LocaleCode): LocaleDefinition => LOCALES[code];

export const getDirection = (code: LocaleCode): Direction => LOCALES[code].direction;

export const isRtl = (code: LocaleCode): boolean => LOCALES[code].direction === 'rtl';

/**
 * Best-effort match of a raw language tag (a stored preference, a browser language) onto a
 * supported locale.
 *
 * Matches exactly first, then by primary subtag, so `ar`, `ar-SA` and `ar-EG` all land on `ar-AE`
 * rather than silently falling back to English. That matters for first-visit users on pre-login
 * screens, where no org preference exists yet.
 */
export const resolveLocaleTag = (tag: string | null | undefined): LocaleCode | null => {
  if (!tag) return null;
  const normalized = tag.trim();
  if (!normalized) return null;

  const exact = LOCALE_CODES.find((c) => c.toLowerCase() === normalized.toLowerCase());
  if (exact) return exact;

  const primary = normalized.split('-')[0].toLowerCase();
  const byPrimary = LOCALE_CODES.find((c) => c.split('-')[0].toLowerCase() === primary);
  return byPrimary ?? null;
};
